import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

function fmtDay(iso) {
  const raw = String(iso || '').trim();
  const ymd = raw.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  const dt = ymd ? new Date(`${ymd}T12:00:00`) : new Date(raw);
  if (Number.isNaN(dt.getTime())) return raw || '';
  return dt.toLocaleDateString('he-IL', {
    weekday: 'short', day: '2-digit', month: '2-digit',
  });
}

export default function PickupPage() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [booking, setBooking] = useState(null);
  const [days, setDays] = useState([]);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [busy, setBusy] = useState(false);
  const [installer, setInstaller] = useState(false);
  const [sameDay, setSameDay] = useState(false);
  const [today, setToday] = useState('');
  const [minDate, setMinDate] = useState('');

  useEffect(() => {
    axios.get(`/api/public/pickup/${token}`)
      .then(({ data }) => {
        setOrder(data.order);
        setBooking(data.booking);
        setInstaller(data.booker_kind === 'installer');
        setSameDay(data.booker_kind === 'same_day');
        setToday(data.today || '');
        setMinDate(data.min_date || '');
        if (data.booking?.date) setDate(data.booking.date);
        if (data.booking?.slot) setSlot(data.booking.slot);
      })
      .catch((err) => setError(err.response?.data?.error || 'הקישור אינו תקף'));
  }, [token]);

  useEffect(() => {
    if (!order) return;
    axios.get(`/api/public/pickup/${token}/days`, { params: date ? { date } : {} })
      .then(({ data }) => {
        setDays(data.days || []);
        setSlots(data.slots || []);
        if (!date && data.days?.[0]) setDate(data.days[0]);
        if (data.booker_kind === 'same_day' && data.date === data.today && data.slots?.length === 1) {
          const only = data.slots[0].start || data.slots[0];
          setSlot(only);
        }
      })
      .catch(() => {});
  }, [token, order, date]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setOk('');
    if (!date || !slot) {
      setError('בחרו תאריך ושעה');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`/api/public/pickup/${token}`, {
        date,
        slot_start: slot,
      });
      setBooking(data.booking);
      setOk(data.lionwheelUpdated === false
        ? 'התור נשמר. עדכון LionWheel נכשל — הצוות יטפל.'
        : 'התור נשמר. מחכים לכם במחסן במועד שבחרתם.');
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירה');
    } finally {
      setBusy(false);
    }
  };

  if (error && !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-md text-center text-red-600">{error}</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nexolog-teal" />
      </div>
    );
  }

  const sameDayToday = sameDay && date && today && date === today;
  const morning = (Array.isArray(slots) ? slots : []).filter((s) => (s.start || s) < '12:00');
  const afternoon = (Array.isArray(slots) ? slots : []).filter((s) => (s.start || s) >= '13:00');

  const renderSlots = (list) => list.map((s) => {
    const start = s.start || s;
    const taken = !!s.taken && start !== booking?.slot;
    const selected = slot === start;
    return (
      <button
        key={start}
        type="button"
        disabled={taken}
        dir="ltr"
        onClick={() => setSlot(start)}
        className={`min-h-[42px] rounded-lg border px-3 text-sm font-medium ${
          taken ? 'bg-gray-100 text-gray-400 border-gray-200'
            : selected ? 'bg-nexolog-teal text-white border-nexolog-teal'
              : 'bg-white border-gray-300 text-nexolog-navy'
        }`}
      >
        {start}
      </button>
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-nexolog-navy text-white py-4 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-2xl font-bold"><span style={{ color: '#1DBFAF' }}>Nexolog</span></div>
          <div className="text-sm text-gray-300">
            {sameDay
              ? 'תיאום איסוף עצמי · מהיום להיום'
              : installer ? 'תיאום איסוף עצמי · לוח מתקינים' : 'תיאום איסוף עצמי'}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <section className="bg-white rounded-xl shadow p-4 space-y-1">
          <h1 className="text-xl font-bold text-nexolog-navy">הזמנה {order.order_id}</h1>
          {order.company && <p className="text-sm text-gray-600">{order.company}</p>}
          <p className="text-sm">נמען: {order.recipient || '—'}</p>
        </section>

        {booking && (
          <div className="bg-teal-50 border border-teal-200 text-teal-800 text-sm rounded-xl p-3">
            תור נוכחי: {fmtDay(booking.date)} בשעה {booking.slot}. אפשר לבחור מועד אחר.
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{error}</div>}
        {ok && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl p-3">{ok}</div>}

        <form onSubmit={submit} className="space-y-4">
          <section className="bg-white rounded-xl shadow p-4 space-y-3">
            <h2 className="font-bold text-nexolog-navy">
              בחירת תאריך ({sameDayToday ? 'היום' : (minDate ? `החל מ-${fmtDay(minDate)}` : 'מהיום למחר')})
            </h2>
            <div className="flex flex-wrap gap-2">
              {days.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => { setDate(d); setSlot(''); }}
                  className={`min-h-[42px] rounded-lg border px-3 text-sm ${
                    date === d ? 'bg-nexolog-navy text-white border-nexolog-navy' : 'bg-white border-gray-300'
                  }`}
                >
                  {fmtDay(d)}
                </button>
              ))}
            </div>
          </section>

          {date && (
            <section className="bg-white rounded-xl shadow p-4 space-y-3">
              <h2 className="font-bold text-nexolog-navy">שעה — {fmtDay(date)}</h2>
              {sameDayToday ? (
                <p className="text-xs text-gray-500">
                  עד 10:30 — 13:30 או 14:00. עד 14:30 — 16:00 או 16:30. עד 2 איסופים לחברה ביום. אחרי 14:30 אין איסוף מהיום.
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  {installer ? 'בוקר 08:00–12:00 · עד 3 איסופים לתור' : (sameDay ? 'בוקר 09:00–12:00 · עד 2 איסופים לכל חצי שעה' : 'בוקר 09:00–12:00')}
                </p>
              )}
              {!sameDayToday && <div className="grid grid-cols-3 gap-2">{renderSlots(morning)}</div>}
              {!sameDayToday && <p className="text-xs text-gray-500 pt-2">צהריים 13:00–17:00</p>}
              <div className="grid grid-cols-3 gap-2">{renderSlots(sameDayToday ? slots : afternoon)}</div>
            </section>
          )}

          <button
            type="submit"
            disabled={busy || !date || !slot}
            className="w-full bg-nexolog-teal hover:bg-nexolog-teal/90 disabled:opacity-50 text-white font-bold py-3 rounded-xl"
          >
            {busy ? 'שומר...' : 'אישור תור לאיסוף'}
          </button>
        </form>
      </main>
    </div>
  );
}
